/* eslint-disable max-len */
import { useMemo } from 'react';
import {
    Flex,
    FormLabel,
    Skeleton,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Select, SkeletonMotion } from '@acp/core-ui';
import { Props } from 'chakra-react-select';
import { BookableServiceV2 } from '@acp/shared';
import { useBookableServices } from '../../../booking/hooks/use-booking';

type ServiceOption = {
    label: string;
    value: string;
};

function BookingServiceSelect({
    propertyId,
    value,
    onChange,
    size,
    returnServiceIds = false,
}: {
    propertyId: number;
    onChange: Props<ServiceOption, false>['onChange'];
    value?: string;
    size?: React.ComponentProps<typeof Select>['size'];
    returnServiceIds?: boolean;
}) {
    const useBookableServicesQuery = useBookableServices({
        propertyId,
    });

    const serviceOptions = useMemo(() => {
        if (useBookableServicesQuery.isLoading) {
            return [];
        }

        const services: BookableServiceV2[] = useBookableServicesQuery.data ?? [];

        return services.map((service) => ({
            label: service.name,
            value: returnServiceIds ? service.services?.join(',') : service.bookingTypes?.join(','),
        }));
    }, [useBookableServicesQuery.data, useBookableServicesQuery.isLoading]);

    const selectedOption = useMemo(() => serviceOptions.find((s) => s.value === String(value)), [serviceOptions, value]);

    return (
        <Select<ServiceOption, false>
            options={serviceOptions}
            onChange={onChange}
            value={selectedOption}
            isLoading={useBookableServicesQuery.isLoading}
            placeholder="Select a service"
            size={size}
        />
    );
}

function BookingServiceSkeleton() {
    return (
        <VStack alignItems="start" opacity={0.4} w="full" gap={4}>
            <SkeletonMotion height={8} width="50%" sx={{ animationDuration: '2s' }} />
            <Skeleton height={28} width="100%" sx={{ animationDuration: '2.4s' }} />
        </VStack>
    );
}

function NoBookingService() {
    return (
        <Flex
            flexDir="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            px={8}
            borderColor="muted.50"
            borderWidth={1}
            rounded="md"
        >
            <Text fontSize="sm" color="black">There is no service available. Please try again later or contact us</Text>
        </Flex>
    );
}

// uses static options, does not perform the fetch action itself
function BookingServiceSelectStatic({
    value,
    onChange,
    size,
    serviceOptions,
    isLoading,
}: {
    onChange: Props<ServiceOption, false>['onChange'];
    value?: string;
    size?: React.ComponentProps<typeof Select>['size'];
    serviceOptions: ServiceOption[];
    isLoading: boolean;
}) {
    const selectedOption = useMemo(() => serviceOptions.find((s) => s.value === String(value)), [serviceOptions, value]);

    if (isLoading) {
        return <BookingServiceSkeleton />;
    }

    if (serviceOptions?.length === 0) {
        return <NoBookingService />;
    }

    return (
        <>
            <FormLabel htmlFor="none">
                Select a service
            </FormLabel>
            <Select<ServiceOption, false>
                options={serviceOptions}
                onChange={onChange}
                value={selectedOption}
                isLoading={isLoading}
                placeholder="Select a service"
                size={size}
            />
        </>
    );
}

export { BookingServiceSelect, BookingServiceSelectStatic };
